import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Logo from '../components/Logo';

const TOC = [
  { id: 'introduction',    icon: 'menu_book',                children: ['what-is-ida', 'why-decentralized-identity', 'standards-compliance', 'trust-model', 'platform-architecture', 'adi-blockchain'] },
  { id: 'getting-started', icon: 'rocket_launch',            children: ['quickstart', 'create-account', 'oauth-login', 'did-auth-login', 'first-did', 'embedded-wallet', 'your-dashboard', 'local-dev-setup'] },
  { id: 'dids',            icon: 'fingerprint',              children: ['what-is-did', 'create-did', 'did-document', 'update-services', 'rotate-key', 'deactivate-did', 'did-delegates', 'did-attributes', 'did-resolver'] },
  { id: 'credentials',     icon: 'verified',                 children: ['what-are-vcs', 'receive-credential', 'manage-credentials', 'create-presentation', 'check-status', 'credential-schemas'] },
  { id: 'zk-identity',     icon: 'shield_lock',              children: ['what-are-zkps', 'supported-predicates', 'generate-zkp', 'share-anchor-zkp', 'verify-zkp', 'onchain-anchoring'] },
  { id: 'issuer-portal',   icon: 'edit_note',                children: ['issuer-onboarding', 'schema-management', 'create-schema', 'single-issuance', 'bulk-issuance', 'revocation-management', 'batch-revocation', 'issuer-analytics'] },
  { id: 'verifier-portal', icon: 'fact_check',               children: ['verifier-onboarding', 'proof-request-builder', 'verification-engine', 'verify-presentation', 'verify-zk-proof', 'verification-history', 'trust-framework', 'trusted-issuers'] },
  { id: 'ai-agents',       icon: 'smart_toy',                children: ['agent-dids', 'agent-spec', 'deploy-agent', 'agent-detail', 'delegation-chains', 'trust-scores', 'capability-tokens', 'agent-discovery', 'mcp-integration', 'a2a-support', 'agent-marketplace', 'agent-audit-log'] },
  { id: 'didcomm',         icon: 'forum',                    children: ['didcomm-overview', 'didcomm-connections', 'didcomm-send-receive', 'didcomm-messages', 'didcomm-service-endpoints', 'didcomm-encryption'] },
  { id: 'wallet',          icon: 'account_balance_wallet',   children: ['wallet-architecture', 'wallet-onboarding', 'wallet-credentials', 'wallet-zkp', 'wallet-didcomm', 'wallet-security', 'wallet-recovery'] },
  { id: 'api-reference',   icon: 'api',                      children: ['api-overview', 'api-auth', 'api-dids', 'api-credentials', 'api-presentations', 'api-zkp', 'api-agents', 'api-didcomm', 'api-dashboard', 'api-issuer-analytics', 'api-verifier', 'api-health', 'api-rate-limits'] },
  { id: 'sdk',             icon: 'integration_instructions', children: ['sdk-install', 'sdk-client', 'sdk-first-call', 'sdk-did-ops', 'sdk-credential-ops', 'sdk-agent-ops', 'sdk-zkp-ops', 'sdk-crypto', 'sdk-types', 'sdk-examples', 'sdk-agent-quickstart', 'sdk-errors'] },
  { id: 'smart-contracts', icon: 'token',                    children: ['contracts-deployment-map', 'did-registry', 'schema-registry', 'revocation-registry', 'agent-trust-registry', 'zk-proof-verifier', 'contracts-interfaces', 'contracts-upgrade'] },
  { id: 'rbac',            icon: 'admin_panel_settings',     children: ['roles-overview', 'permission-matrix', 'role-navigation', 'server-enforcement', 'jwt-lifecycle', 'api-keys', 'security-controls'] },
  { id: 'deployment',      icon: 'cloud',                    children: ['system-requirements', 'docker-setup', 'kubernetes-setup', 'configuration', 'env-vars', 'migrations-seed', 'contract-deployment', 'observability', 'backups-dr', 'scaling-notes'] },
  { id: 'troubleshooting', icon: 'troubleshoot',             children: ['common-errors', 'did-resolution-issues', 'credential-failures', 'zkp-issues', 'agent-issues', 'didcomm-issues', 'rpc-issues', 'wallet-recovery-issues', 'faq'] },
  { id: 'glossary',        icon: 'menu_book',                children: ['glossary-a-f', 'glossary-g-m', 'glossary-n-s', 'glossary-t-z'] },
];

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 overflow-hidden rounded-lg border border-slate-800">
      {title && (
        <div className="border-b border-slate-800 bg-slate-950 px-4 py-2 text-xs font-semibold text-slate-400">
          {title}
        </div>
      )}
      <pre className="overflow-x-auto bg-slate-900 px-4 py-3 text-sm leading-relaxed text-emerald-400 font-mono">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    POST: 'bg-blue-100 text-blue-700 border-blue-200',
    PUT: 'bg-amber-100 text-amber-700 border-amber-200',
    PATCH: 'bg-amber-100 text-amber-700 border-amber-200',
    DELETE: 'bg-red-100 text-red-700 border-red-200',
  };
  return (
    <span className={`inline-block rounded border px-2 py-0.5 text-xs font-bold uppercase tracking-wide ${colors[method] ?? 'bg-slate-100 text-slate-600 border-slate-200'}`}>
      {method}
    </span>
  );
}

function ApiEndpoint({ method, path, description }: { method: string; path: string; description: string }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border-subtle bg-white p-4 shadow-sm">
      <MethodBadge method={method} />
      <div className="min-w-0 flex-1">
        <code className="text-sm font-semibold text-text-main">{path}</code>
        <p className="mt-1 text-sm text-text-muted">{description}</p>
      </div>
    </div>
  );
}

function SectionHeading({ id, icon, children }: { id: string; icon: string; children: React.ReactNode }) {
  const { t } = useTranslation('common');
  return (
    <h2 id={id} className="group mt-16 mb-6 flex items-center gap-3 scroll-mt-24 text-2xl font-bold tracking-tight text-text-main">
      <span className="material-symbols-outlined text-primary" style={{ fontSize: 28, fontVariationSettings: "'FILL' 1" }}>
        {icon}
      </span>
      {children}
      <a href={`#${id}`} className="ml-2 text-text-faint opacity-0 transition-opacity group-hover:opacity-100" aria-label={t('linkToSection')}>
        <span className="material-symbols-outlined" style={{ fontSize: 20 }}>link</span>
      </a>
    </h2>
  );
}

function SubHeading({ id, children }: { id: string; children: React.ReactNode }) {
  const { t } = useTranslation('common');
  return (
    <h3 id={id} className="group mt-10 mb-3 scroll-mt-24 text-lg font-semibold text-text-main">
      {children}
      <a href={`#${id}`} className="ml-2 text-text-faint opacity-0 transition-opacity group-hover:opacity-100" aria-label={t('linkToSection')}>
        <span className="material-symbols-outlined align-middle" style={{ fontSize: 16 }}>link</span>
      </a>
    </h3>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return <p className="mb-4 leading-relaxed text-text-muted">{children}</p>;
}

function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="my-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
      <span className="material-symbols-outlined mt-0.5 text-primary" style={{ fontSize: 20 }}>info</span>
      <div className="text-sm leading-relaxed text-slate-700">{children}</div>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="mb-4 ml-6 list-disc space-y-1 text-text-muted">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ul>
  );
}

function NumberList({ items }: { items: string[] }) {
  return (
    <ol className="mb-4 ml-6 list-decimal space-y-2 text-text-muted">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ol>
  );
}

function SimpleTable({ header, rows }: { header: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-lg border border-border-subtle">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border-subtle bg-slate-50">
            {header.map((h, i) => (
              <th key={i} className="px-4 py-3 text-left font-semibold text-text-main">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="text-text-muted">
          {rows.map((row, ri) => (
            <tr key={ri} className="border-b border-border-subtle last:border-0">
              {row.map((cell, ci) => (
                <td key={ci} className="px-4 py-3 align-top">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const roleColorMap: Record<string, { color: string; icon: string }> = {
  Admin: { color: 'text-red-600 bg-red-50 border-red-200', icon: 'shield_person' },
  Issuer: { color: 'text-blue-600 bg-blue-50 border-blue-200', icon: 'edit_note' },
  Verifier: { color: 'text-amber-600 bg-amber-50 border-amber-200', icon: 'fact_check' },
  Holder: { color: 'text-emerald-600 bg-emerald-50 border-emerald-200', icon: 'person' },
};

export default function Docs() {
  const [activeSection, setActiveSection] = useState('introduction');
  const { t } = useTranslation('docs');

  useEffect(() => {
    const ids = TOC.flatMap((s) => [s.id, ...s.children]);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0.1 },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const isGroupActive = (group: (typeof TOC)[number]) =>
    activeSection === group.id || group.children.some((id) => id === activeSection);

  const arr = (key: string) => (t(key, { returnObjects: true, defaultValue: [] }) as unknown) as string[];
  const arr2 = (key: string) => (t(key, { returnObjects: true, defaultValue: [] }) as unknown) as string[][];
  const obj = <T,>(key: string) => (t(key, { returnObjects: true, defaultValue: [] }) as unknown) as T;

  const capabilities = arr('sections.capabilities');
  const standardsList = arr('sections.standardsList');
  const platformArchitectureLayers = arr('sections.platformArchitectureLayers');
  const trustModelRoles = obj<{ role: string; desc: string }[]>('sections.trustModelRoles');
  const createSteps = arr('sections.createAccountSteps');
  const dashboardWidgets = arr('sections.dashboardWidgets');
  const createDidSteps = arr('sections.createDidSteps');
  const issueSteps = arr('sections.issueSteps');
  const generateZkpSteps = arr('sections.generateZkpSteps');
  const deployAgentSteps = arr('sections.deployAgentSteps');
  const apiAuthList = arr('sections.apiAuthList');
  const predicateRows = arr2('sections.predicateTable.rows');
  const predicateHeader = arr('sections.predicateTable.header');
  const autonomyRows = arr2('sections.autonomyTable.rows');
  const autonomyHeader = arr('sections.autonomyTable.header');
  const roles = obj<{ role: string; desc: string }[]>('sections.roles');
  const permissionRows = (t('sections.permissionTable.rows', { returnObjects: true, defaultValue: [] }) as Array<Array<string | boolean>>) ?? [];
  const permissionHeader = arr('sections.permissionTable.header');

  const envConsumedHeader = arr('sections.envVarsConsumedHeader');
  const envConsumedRows = arr2('sections.envVarsConsumedRows');
  const envInformationalHeader = arr('sections.envVarsInformationalHeader');
  const envInformationalRows = arr2('sections.envVarsInformationalRows');

  const troubleshootingHeader = arr('sections.troubleshootingHeader');
  const troubleshootingRows = arr2('sections.troubleshootingRows');
  const faqHeader = arr('sections.faqHeader');
  const faqRows = arr2('sections.faqRows');

  const glossaryAfRows = arr2('sections.glossaryAfRows');
  const glossaryGmRows = arr2('sections.glossaryGmRows');
  const glossaryNsRows = arr2('sections.glossaryNsRows');
  const glossaryTzRows = arr2('sections.glossaryTzRows');

  const contractsDeploymentMapHeader = arr('sections.contractsDeploymentMapHeader');
  const contractsDeploymentMapRows = arr2('sections.contractsDeploymentMapRows');

  const endpoints = {
    auth: obj<Array<{ method: string; path: string; description: string }>>('sections.endpoints.auth'),
    dids: obj<Array<{ method: string; path: string; description: string }>>('sections.endpoints.dids'),
    credentials: obj<Array<{ method: string; path: string; description: string }>>('sections.endpoints.credentials'),
    presentations: obj<Array<{ method: string; path: string; description: string }>>('sections.endpoints.presentations'),
    zkp: obj<Array<{ method: string; path: string; description: string }>>('sections.endpoints.zkp'),
    agents: obj<Array<{ method: string; path: string; description: string }>>('sections.endpoints.agents'),
    didcomm: obj<Array<{ method: string; path: string; description: string }>>('sections.endpoints.didcomm'),
    dashboard: obj<Array<{ method: string; path: string; description: string }>>('sections.endpoints.dashboard'),
    issuerAnalytics: obj<Array<{ method: string; path: string; description: string }>>('sections.endpoints.issuerAnalytics'),
    verifier: obj<Array<{ method: string; path: string; description: string }>>('sections.endpoints.verifier'),
    health: obj<Array<{ method: string; path: string; description: string }>>('sections.endpoints.health'),
  };

  return (
    <div className="min-h-screen bg-background-light font-sans">
      <header className="sticky top-0 z-30 border-b border-border-subtle bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-screen-2xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <Logo className="h-7 w-auto text-text-main" />
            <span className="h-5 w-px bg-border-subtle" aria-hidden="true" />
            <span className="text-lg font-bold tracking-tight text-text-faint">
              {t('header.section')}
            </span>
          </div>
          <Link to="/dashboard" className="btn-secondary text-sm">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
            {t('header.back')}
          </Link>
        </div>
      </header>

      <div className="mx-auto flex max-w-screen-2xl">
        <aside className="sticky top-[57px] hidden h-[calc(100vh-57px)] w-72 shrink-0 overflow-y-auto border-r border-border-subtle bg-white px-4 py-6 lg:block">
          <p className="mb-4 px-2 text-[10px] font-bold uppercase tracking-widest text-text-faint">{t('toc.label')}</p>
          <nav className="space-y-1">
            {TOC.map((group) => (
              <div key={group.id}>
                <button
                  onClick={() => scrollTo(group.id)}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-semibold transition-colors ${
                    isGroupActive(group)
                      ? 'bg-blue-50 text-primary'
                      : 'text-text-muted hover:bg-slate-50 hover:text-text-main'
                  }`}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18, fontVariationSettings: isGroupActive(group) ? "'FILL' 1" : "'FILL' 0" }}>
                    {group.icon}
                  </span>
                  {t(`toc.groups.${group.id}`)}
                </button>
                {isGroupActive(group) && (
                  <div className="ml-7 mt-1 space-y-0.5 border-l-2 border-blue-100 pl-3">
                    {group.children.map((childId) => (
                      <button
                        key={childId}
                        onClick={() => scrollTo(childId)}
                        className={`block w-full rounded px-2 py-1 text-left text-[13px] transition-colors ${
                          activeSection === childId
                            ? 'font-semibold text-primary'
                            : 'text-text-muted hover:text-text-main'
                        }`}
                      >
                        {t(`toc.items.${childId}`)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1 px-8 py-10 lg:px-16">
          <div className="mb-12 max-w-3xl">
            <h1 className="text-4xl font-extrabold tracking-tight text-text-main">{t('hero.title')}</h1>
            <p className="mt-3 text-lg leading-relaxed text-text-muted">{t('hero.subtitle')}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button onClick={() => scrollTo('introduction')} className="btn-primary">
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>play_arrow</span>
                {t('hero.getStarted')}
              </button>
              <button onClick={() => scrollTo('api-reference')} className="btn-secondary">
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>api</span>
                {t('hero.apiReference')}
              </button>
            </div>
          </div>

          {/* === INTRODUCTION === */}
          <SectionHeading id="introduction" icon="menu_book">{t('sections.introductionHeading')}</SectionHeading>

          <SubHeading id="what-is-ida">{t('sections.whatIsIda')}</SubHeading>
          <Para>{t('sections.whatIsIdaP1')}</Para>
          <Para>{t('sections.whatIsIdaP2')}</Para>
          <BulletList items={capabilities} />

          <SubHeading id="why-decentralized-identity">{t('sections.whyDecentralized')}</SubHeading>
          <Para>{t('sections.whyDecentralizedP1')}</Para>
          <Para>{t('sections.whyDecentralizedP2')}</Para>

          <SubHeading id="standards-compliance">{t('sections.standardsCompliance')}</SubHeading>
          <Para>{t('sections.standardsComplianceP')}</Para>
          <BulletList items={standardsList} />

          <SubHeading id="trust-model">{t('sections.trustModel')}</SubHeading>
          <Para>{t('sections.trustModelP')}</Para>
          <div className="my-4 grid gap-4 sm:grid-cols-2">
            {trustModelRoles.map((r) => (
              <div key={r.role} className="rounded-lg border border-border-subtle bg-white p-4">
                <div className="font-bold text-text-main">{r.role}</div>
                <p className="mt-2 text-sm text-text-muted">{r.desc}</p>
              </div>
            ))}
          </div>

          <SubHeading id="platform-architecture">{t('sections.platformArchitecture')}</SubHeading>
          <Para>{t('sections.platformArchitectureP')}</Para>
          <BulletList items={platformArchitectureLayers} />

          <SubHeading id="adi-blockchain">{t('sections.adiBlockchain')}</SubHeading>
          <Para>{t('sections.adiBlockchainP')}</Para>

          {/* === GETTING STARTED === */}
          <SectionHeading id="getting-started" icon="rocket_launch">{t('sections.gettingStarted')}</SectionHeading>

          <SubHeading id="quickstart">{t('sections.quickstart')}</SubHeading>
          <Para>{t('sections.quickstartP')}</Para>

          <SubHeading id="create-account">{t('sections.createAccount')}</SubHeading>
          <Para>{t('sections.createAccountP')}</Para>
          <NumberList items={createSteps} />
          <InfoBox>{t('sections.createAccountInfo')}</InfoBox>

          <SubHeading id="oauth-login">{t('sections.oauthLogin')}</SubHeading>
          <Para>{t('sections.oauthLoginP')}</Para>
          <CodeBlock title={t('sections.oauthCallbackTitle')}>{`https://adid.dev/auth/callback?provider=google
https://adid.dev/auth/callback?provider=github`}</CodeBlock>

          <SubHeading id="did-auth-login">{t('sections.didAuthLogin')}</SubHeading>
          <Para>{t('sections.didAuthLoginP')}</Para>
          <CodeBlock title="DID-Auth flow">{`# 1. Get a nonce
curl -X POST https://adid.dev/api/v1/auth/challenge \\
     -H 'Content-Type: application/json' -d '{"did":"did:adi:0x..."}'

# 2. Sign the nonce with your DID key (client-side)
# 3. Submit signed payload
curl -X POST https://adid.dev/api/v1/auth/verify-otp \\
     -H 'Content-Type: application/json' \\
     -d '{"did":"did:adi:0x...","signature":"...","nonce":"..."}'`}</CodeBlock>

          <SubHeading id="first-did">{t('sections.firstDid')}</SubHeading>
          <Para>{t('sections.firstDidP')}</Para>

          <SubHeading id="embedded-wallet">{t('sections.embeddedWallet')}</SubHeading>
          <Para>{t('sections.embeddedWalletP')}</Para>

          <SubHeading id="your-dashboard">{t('sections.yourDashboard')}</SubHeading>
          <Para>{t('sections.yourDashboardP')}</Para>
          <BulletList items={dashboardWidgets} />

          <SubHeading id="local-dev-setup">{t('sections.localDevSetup')}</SubHeading>
          <Para>{t('sections.localDevSetupP')}</Para>
          <CodeBlock title="Local dev (one-shot)">{`git clone https://github.com/infinia/ida.git
cd ida
make dev
# API on :8080, portal on :5173, hardhat on :8545`}</CodeBlock>

          {/* === DIDs === */}
          <SectionHeading id="dids" icon="fingerprint">{t('sections.didsHeading')}</SectionHeading>

          <SubHeading id="what-is-did">{t('sections.whatIsDid')}</SubHeading>
          <Para>{t('sections.whatIsDidP')}</Para>
          <CodeBlock title={t('sections.exampleDidTitle')}>{`did:adi:0x7a3b...4f2e`}</CodeBlock>

          <SubHeading id="create-did">{t('sections.createDid')}</SubHeading>
          <Para>{t('sections.createDidP')}</Para>
          <NumberList items={createDidSteps} />

          <SubHeading id="did-document">{t('sections.didDocument')}</SubHeading>
          <Para>{t('sections.didDocumentP')}</Para>
          <CodeBlock title={t('sections.exampleDidDocTitle')}>{`{
  "@context": "https://www.w3.org/ns/did/v1",
  "id": "did:adi:0x7a3b...4f2e",
  "verificationMethod": [{
    "id": "did:adi:0x7a3b...4f2e#key-1",
    "type": "Ed25519VerificationKey2020",
    "controller": "did:adi:0x7a3b...4f2e",
    "publicKeyMultibase": "z6Mkf5rG..."
  }],
  "authentication": ["did:adi:0x7a3b...4f2e#key-1"],
  "service": [{
    "id": "did:adi:0x7a3b...4f2e#credential-exchange",
    "type": "CredentialExchange",
    "serviceEndpoint": "https://adid.dev/api/v1/exchange"
  }]
}`}</CodeBlock>

          <SubHeading id="update-services">{t('sections.updateServices')}</SubHeading>
          <Para>{t('sections.updateServicesP')}</Para>

          <SubHeading id="rotate-key">{t('sections.rotateKey')}</SubHeading>
          <Para>{t('sections.rotateKeyP')}</Para>
          <InfoBox>{t('sections.rotateKeyInfo')}</InfoBox>

          <SubHeading id="deactivate-did">{t('sections.deactivateDid')}</SubHeading>
          <Para>{t('sections.deactivateDidP')}</Para>

          <SubHeading id="did-delegates">{t('sections.didDelegates')}</SubHeading>
          <Para>{t('sections.didDelegatesP')}</Para>

          <SubHeading id="did-attributes">{t('sections.didAttributes')}</SubHeading>
          <Para>{t('sections.didAttributesP')}</Para>

          <SubHeading id="did-resolver">{t('sections.didResolver')}</SubHeading>
          <Para>{t('sections.didResolverP')}</Para>

          {/* === CREDENTIALS === */}
          <SectionHeading id="credentials" icon="verified">{t('sections.credentialsHeading')}</SectionHeading>

          <SubHeading id="what-are-vcs">{t('sections.whatAreVcs')}</SubHeading>
          <Para>{t('sections.whatAreVcsP1')}</Para>
          <Para>{t('sections.whatAreVcsP2')}</Para>

          <SubHeading id="receive-credential">{t('sections.receiveCredential')}</SubHeading>
          <Para>{t('sections.receiveCredentialP')}</Para>

          <SubHeading id="manage-credentials">{t('sections.manageCredentials')}</SubHeading>
          <Para>{t('sections.manageCredentialsP')}</Para>

          <SubHeading id="create-presentation">{t('sections.createPresentation')}</SubHeading>
          <Para>{t('sections.createPresentationP')}</Para>

          <SubHeading id="check-status">{t('sections.checkStatus')}</SubHeading>
          <Para>{t('sections.checkStatusP')}</Para>

          <SubHeading id="credential-schemas">{t('sections.credentialSchemas')}</SubHeading>
          <Para>{t('sections.credentialSchemasP')}</Para>
          <CodeBlock title={t('sections.exampleSchemaTitle')}>{`{
  "name": "KYC Verification",
  "version": "1.0",
  "attributes": [
    { "name": "fullName",    "type": "string",  "required": true  },
    { "name": "dateOfBirth", "type": "date",    "required": true  },
    { "name": "country",     "type": "string",  "required": true  },
    { "name": "idNumber",    "type": "string",  "required": true  },
    { "name": "level",       "type": "integer", "required": false }
  ]
}`}</CodeBlock>

          {/* === ZK IDENTITY === */}
          <SectionHeading id="zk-identity" icon="shield_lock">{t('sections.zkHeading')}</SectionHeading>

          <SubHeading id="what-are-zkps">{t('sections.whatAreZkps')}</SubHeading>
          <Para>{t('sections.whatAreZkpsP1')}</Para>
          <Para>{t('sections.whatAreZkpsP2')}</Para>

          <SubHeading id="supported-predicates">{t('sections.supportedPredicates')}</SubHeading>
          <div className="my-4 overflow-hidden rounded-lg border border-border-subtle">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-subtle bg-slate-50">
                  {predicateHeader.map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left font-semibold text-text-main">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-text-muted">
                {predicateRows.map((row, ri) => (
                  <tr key={ri} className={ri < predicateRows.length - 1 ? 'border-b border-border-subtle' : ''}>
                    <td className="px-4 py-3 font-mono text-sm text-primary">{row[0]}</td>
                    <td className="px-4 py-3"><code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-mono">{row[1]}</code></td>
                    <td className="px-4 py-3">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <SubHeading id="generate-zkp">{t('sections.generateZkp')}</SubHeading>
          <NumberList items={generateZkpSteps} />

          <SubHeading id="share-anchor-zkp">{t('sections.shareAnchorZkp')}</SubHeading>
          <Para>{t('sections.shareAnchorZkpP')}</Para>

          <SubHeading id="verify-zkp">{t('sections.verifyZkp')}</SubHeading>
          <Para>{t('sections.verifyZkpP')}</Para>
          <CodeBlock title={t('sections.verifyZkpApiTitle')}>{`POST /api/v1/zkp/verify
Content-Type: application/json

{
  "proof": "0xabc123...",
  "publicSignals": ["18", "1"],
  "predicateType": "age_gte"
}`}</CodeBlock>

          <SubHeading id="onchain-anchoring">{t('sections.onchain')}</SubHeading>
          <Para>{t('sections.onchainP')}</Para>
          <InfoBox>{t('sections.onchainInfo')}</InfoBox>

          {/* === ISSUER PORTAL === */}
          <SectionHeading id="issuer-portal" icon="edit_note">{t('sections.issuerPortalHeading')}</SectionHeading>

          <SubHeading id="issuer-onboarding">{t('sections.issuerOnboarding')}</SubHeading>
          <Para>{t('sections.issuerOnboardingP')}</Para>

          <SubHeading id="schema-management">{t('sections.schemaManagement')}</SubHeading>
          <Para>{t('sections.schemaManagementP')}</Para>

          <SubHeading id="create-schema">{t('sections.createSchema')}</SubHeading>
          <Para>{t('sections.createSchemaP')}</Para>

          <SubHeading id="single-issuance">{t('sections.singleIssuance')}</SubHeading>
          <Para>{t('sections.singleIssuanceP')}</Para>
          <NumberList items={issueSteps} />
          <InfoBox>{t('sections.issueInfo')}</InfoBox>

          <SubHeading id="bulk-issuance">{t('sections.bulkIssuance')}</SubHeading>
          <Para>{t('sections.bulkIssuanceP')}</Para>

          <SubHeading id="revocation-management">{t('sections.revocationManagement')}</SubHeading>
          <Para>{t('sections.revocationManagementP')}</Para>

          <SubHeading id="batch-revocation">{t('sections.batchRevocation')}</SubHeading>
          <Para>{t('sections.batchRevocationP')}</Para>

          <SubHeading id="issuer-analytics">{t('sections.issuerAnalytics')}</SubHeading>
          <Para>{t('sections.issuerAnalyticsP')}</Para>

          {/* === VERIFIER PORTAL === */}
          <SectionHeading id="verifier-portal" icon="fact_check">{t('sections.verifierPortalHeading')}</SectionHeading>

          <SubHeading id="verifier-onboarding">{t('sections.verifierOnboarding')}</SubHeading>
          <Para>{t('sections.verifierOnboardingP')}</Para>

          <SubHeading id="proof-request-builder">{t('sections.proofRequestBuilder')}</SubHeading>
          <Para>{t('sections.proofRequestBuilderP')}</Para>

          <SubHeading id="verification-engine">{t('sections.verificationEngine')}</SubHeading>
          <Para>{t('sections.verificationEngineP')}</Para>

          <SubHeading id="verify-presentation">{t('sections.verifyPresentation')}</SubHeading>
          <Para>{t('sections.verifyPresentationP')}</Para>

          <SubHeading id="verify-zk-proof">{t('sections.verifyZkProof')}</SubHeading>
          <Para>{t('sections.verifyZkProofP')}</Para>

          <SubHeading id="verification-history">{t('sections.verificationHistory')}</SubHeading>
          <Para>{t('sections.verificationHistoryP')}</Para>

          <SubHeading id="trust-framework">{t('sections.trustFramework')}</SubHeading>
          <Para>{t('sections.trustFrameworkP')}</Para>

          <SubHeading id="trusted-issuers">{t('sections.trustedIssuers')}</SubHeading>
          <Para>{t('sections.trustedIssuersP')}</Para>

          {/* === AI AGENTS === */}
          <SectionHeading id="ai-agents" icon="smart_toy">{t('sections.agentsHeading')}</SectionHeading>

          <SubHeading id="agent-dids">{t('sections.agentDids')}</SubHeading>
          <Para>{t('sections.agentDidsP')}</Para>

          <SubHeading id="agent-spec">{t('sections.agentSpec')}</SubHeading>
          <Para>{t('sections.agentSpecP')}</Para>

          <SubHeading id="deploy-agent">{t('sections.deployAgent')}</SubHeading>
          <NumberList items={deployAgentSteps} />

          <SubHeading id="agent-detail">{t('sections.agentDetail')}</SubHeading>
          <Para>{t('sections.agentDetailP')}</Para>

          <SubHeading id="delegation-chains">{t('sections.delegationChains')}</SubHeading>
          <Para>{t('sections.delegationChainsP')}</Para>
          <CodeBlock title={t('sections.delegationCodeTitle')}>{`Human DID (root)
  └── Agent DID (level 1, autonomy: 4)
        ├── Sub-Agent DID (level 2, autonomy: 2)
        └── Sub-Agent DID (level 2, autonomy: 3)`}</CodeBlock>

          <SubHeading id="trust-scores">{t('sections.trustScores')}</SubHeading>
          <Para>{t('sections.trustScoresP')}</Para>
          <div className="my-4 overflow-hidden rounded-lg border border-border-subtle">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-subtle bg-slate-50">
                  {autonomyHeader.map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left font-semibold text-text-main">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-text-muted">
                {autonomyRows.map((row, ri) => (
                  <tr key={ri} className={ri < autonomyRows.length - 1 ? 'border-b border-border-subtle' : ''}>
                    <td className="px-4 py-3 font-semibold text-text-main">{row[0]}</td>
                    <td className="px-4 py-3">{row[1]}</td>
                    <td className="px-4 py-3">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <SubHeading id="capability-tokens">{t('sections.capabilityTokens')}</SubHeading>
          <Para>{t('sections.capabilityTokensP')}</Para>

          <SubHeading id="agent-discovery">{t('sections.agentDiscovery')}</SubHeading>
          <Para>{t('sections.agentDiscoveryP')}</Para>

          <SubHeading id="mcp-integration">{t('sections.mcpIntegration')}</SubHeading>
          <Para>{t('sections.mcpIntegrationP')}</Para>

          <SubHeading id="a2a-support">{t('sections.a2aSupport')}</SubHeading>
          <Para>{t('sections.a2aSupportP')}</Para>

          <SubHeading id="agent-marketplace">{t('sections.agentMarketplace')}</SubHeading>
          <Para>{t('sections.agentMarketplaceP')}</Para>

          <SubHeading id="agent-audit-log">{t('sections.agentAuditLog')}</SubHeading>
          <Para>{t('sections.agentAuditLogP')}</Para>

          {/* === DIDCOMM === */}
          <SectionHeading id="didcomm" icon="forum">{t('sections.didcommHeading')}</SectionHeading>

          <SubHeading id="didcomm-overview">{t('sections.didcommOverview')}</SubHeading>
          <Para>{t('sections.didcommOverviewP')}</Para>

          <SubHeading id="didcomm-connections">{t('sections.didcommConnections')}</SubHeading>
          <Para>{t('sections.didcommConnectionsP')}</Para>

          <SubHeading id="didcomm-send-receive">{t('sections.didcommSendReceive')}</SubHeading>
          <Para>{t('sections.didcommSendReceiveP')}</Para>

          <SubHeading id="didcomm-messages">{t('sections.didcommMessages')}</SubHeading>
          <Para>{t('sections.didcommMessagesP')}</Para>

          <SubHeading id="didcomm-service-endpoints">{t('sections.didcommServiceEndpoints')}</SubHeading>
          <Para>{t('sections.didcommServiceEndpointsP')}</Para>

          <SubHeading id="didcomm-encryption">{t('sections.didcommEncryption')}</SubHeading>
          <Para>{t('sections.didcommEncryptionP')}</Para>

          {/* === WALLET === */}
          <SectionHeading id="wallet" icon="account_balance_wallet">{t('sections.walletHeading')}</SectionHeading>

          <SubHeading id="wallet-architecture">{t('sections.walletArchitecture')}</SubHeading>
          <Para>{t('sections.walletArchitectureP')}</Para>

          <SubHeading id="wallet-onboarding">{t('sections.walletOnboarding')}</SubHeading>
          <Para>{t('sections.walletOnboardingP')}</Para>

          <SubHeading id="wallet-credentials">{t('sections.walletCredentials')}</SubHeading>
          <Para>{t('sections.walletCredentialsP')}</Para>

          <SubHeading id="wallet-zkp">{t('sections.walletZkp')}</SubHeading>
          <Para>{t('sections.walletZkpP')}</Para>

          <SubHeading id="wallet-didcomm">{t('sections.walletDidcomm')}</SubHeading>
          <Para>{t('sections.walletDidcommP')}</Para>

          <SubHeading id="wallet-security">{t('sections.walletSecurity')}</SubHeading>
          <Para>{t('sections.walletSecurityP')}</Para>
          <InfoBox>{t('sections.walletSecurityInfo')}</InfoBox>

          <SubHeading id="wallet-recovery">{t('sections.walletRecovery')}</SubHeading>
          <Para>{t('sections.walletRecoveryP')}</Para>
          <CodeBlock title={t('sections.walletMappingTitle')}>{`Wallet Address: 0x7a3b...4f2e
Primary DID:    did:adi:0x7a3b...4f2e
Registry Tx:    0xabc123...def456`}</CodeBlock>

          {/* === API REFERENCE === */}
          <SectionHeading id="api-reference" icon="api">{t('sections.apiHeading')}</SectionHeading>
          <Para>{t('sections.apiHeadingP')}</Para>
          <CodeBlock title={t('sections.baseUrlTitle')}>{`https://adid.dev/api/v1`}</CodeBlock>

          <SubHeading id="api-overview">{t('sections.apiOverview')}</SubHeading>
          <Para>{t('sections.apiOverviewP')}</Para>

          <SubHeading id="api-auth">{t('sections.apiAuth')}</SubHeading>
          <Para>{t('sections.apiAuthP')}</Para>
          <BulletList items={apiAuthList} />
          <CodeBlock title={t('sections.apiAuthHeaderTitle')}>{`Authorization: Bearer <your-jwt-token>
# or
X-API-Key: <your-api-key>`}</CodeBlock>
          <div className="space-y-3">
            {endpoints.auth.map((e, i) => <ApiEndpoint key={i} {...e} />)}
          </div>

          <SubHeading id="api-dids">{t('toc.items.api-dids')}</SubHeading>
          <div className="space-y-3">
            {endpoints.dids.map((e, i) => <ApiEndpoint key={i} {...e} />)}
          </div>

          <SubHeading id="api-credentials">{t('toc.items.api-credentials')}</SubHeading>
          <div className="space-y-3">
            {endpoints.credentials.map((e, i) => <ApiEndpoint key={i} {...e} />)}
          </div>

          <SubHeading id="api-presentations">{t('toc.items.api-presentations')}</SubHeading>
          <div className="space-y-3">
            {endpoints.presentations.map((e, i) => <ApiEndpoint key={i} {...e} />)}
          </div>

          <SubHeading id="api-zkp">{t('toc.items.api-zkp')}</SubHeading>
          <div className="space-y-3">
            {endpoints.zkp.map((e, i) => <ApiEndpoint key={i} {...e} />)}
          </div>

          <SubHeading id="api-agents">{t('toc.items.api-agents')}</SubHeading>
          <div className="space-y-3">
            {endpoints.agents.map((e, i) => <ApiEndpoint key={i} {...e} />)}
          </div>

          <SubHeading id="api-didcomm">{t('toc.items.api-didcomm')}</SubHeading>
          <div className="space-y-3">
            {endpoints.didcomm.map((e, i) => <ApiEndpoint key={i} {...e} />)}
          </div>

          <SubHeading id="api-dashboard">{t('toc.items.api-dashboard')}</SubHeading>
          <div className="space-y-3">
            {endpoints.dashboard.map((e, i) => <ApiEndpoint key={i} {...e} />)}
          </div>

          <SubHeading id="api-issuer-analytics">{t('toc.items.api-issuer-analytics')}</SubHeading>
          <div className="space-y-3">
            {endpoints.issuerAnalytics.map((e, i) => <ApiEndpoint key={i} {...e} />)}
          </div>

          <SubHeading id="api-verifier">{t('toc.items.api-verifier')}</SubHeading>
          <div className="space-y-3">
            {endpoints.verifier.map((e, i) => <ApiEndpoint key={i} {...e} />)}
          </div>

          <SubHeading id="api-health">{t('toc.items.api-health')}</SubHeading>
          <div className="space-y-3">
            {endpoints.health.map((e, i) => <ApiEndpoint key={i} {...e} />)}
          </div>

          <SubHeading id="api-rate-limits">{t('sections.apiRateLimits')}</SubHeading>
          <Para>{t('sections.apiRateLimitsP')}</Para>

          {/* === SDK === */}
          <SectionHeading id="sdk" icon="integration_instructions">{t('sections.sdkHeading')}</SectionHeading>

          <SubHeading id="sdk-install">{t('sections.sdkInstall')}</SubHeading>
          <Para>{t('sections.sdkInstallP')}</Para>
          <CodeBlock title="Install">{`npm install @myida/sdk
# or: yarn add @myida/sdk  /  pnpm add @myida/sdk`}</CodeBlock>

          <SubHeading id="sdk-client">{t('sections.sdkClient')}</SubHeading>
          <Para>{t('sections.sdkClientP')}</Para>
          <CodeBlock title="Instantiate">{`import { IDAClient } from '@myida/sdk';

const client = new IDAClient({
  apiUrl: 'https://your-ida-instance.example.com',
  apiKey: process.env.IDA_API_KEY,   // API key or JWT — detected automatically
  timeout: 30000,                     // optional, defaults to 30s
});`}</CodeBlock>
          <InfoBox>{t('sections.sdkAuthInfo')}</InfoBox>

          <SubHeading id="sdk-first-call">{t('sections.sdkFirstCall')}</SubHeading>
          <Para>{t('sections.sdkFirstCallP')}</Para>
          <CodeBlock title="Create your first DID">{`// Server-managed keypair — the private key is returned ONCE and never stored.
const { did, keys } = await client.createDID({ keyType: 'ed25519' });

console.log(did);              // did:adi:8fKvmqpjz8HSQ9w...
saveSecurely(keys.privateKey); // you cannot retrieve this later

// Anyone can resolve it — no account, no API key required.
const doc = await client.resolveDID(did);`}</CodeBlock>

          <SubHeading id="sdk-did-ops">{t('sections.sdkDidOps')}</SubHeading>
          <Para>{t('sections.sdkDidOpsP')}</Para>

          <SubHeading id="sdk-credential-ops">{t('sections.sdkCredentialOps')}</SubHeading>
          <Para>{t('sections.sdkCredentialOpsP')}</Para>

          <SubHeading id="sdk-agent-ops">{t('sections.sdkAgentOps')}</SubHeading>
          <Para>{t('sections.sdkAgentOpsP')}</Para>

          <SubHeading id="sdk-zkp-ops">{t('sections.sdkZkpOps')}</SubHeading>
          <Para>{t('sections.sdkZkpOpsP')}</Para>

          <SubHeading id="sdk-crypto">{t('sections.sdkCrypto')}</SubHeading>
          <Para>{t('sections.sdkCryptoP')}</Para>

          <SubHeading id="sdk-types">{t('sections.sdkTypes')}</SubHeading>
          <Para>{t('sections.sdkTypesP')}</Para>

          <SubHeading id="sdk-examples">{t('sections.sdkExamples')}</SubHeading>
          <Para>{t('sections.sdkExamplesP')}</Para>
          <CodeBlock title="Issue → Present → Verify (round-trip)">{`// 1. Issuer makes a statement about a subject
const vc = await client.issueCredential({
  issuerDid: 'did:adi:0xissuer...',
  issuerName: 'Acme Corp',
  subjectDid: 'did:adi:0xholder...',
  type: ['VerifiableCredential', 'EmploymentCredential'],
  credentialSubject: { employeeName: 'Alice', department: 'Finance' },
  schemaId: 'employment-v1',       // optional but recommended
});

// 2. Holder bundles it into a presentation for a specific verifier
const vp = await client.createPresentation({
  holderDid: 'did:adi:0xholder...',
  credentialIds: [vc.id],
  challenge: 'verifier-supplied-nonce',   // replay protection
});

// 3. Verifier checks it — no call back to the issuer
const result = await client.verifyPresentation(vp);
console.log(result.valid, result.checks);`}</CodeBlock>

          <SubHeading id="sdk-agent-quickstart">{t('sections.sdkAgentQuickstart')}</SubHeading>
          <Para>{t('sections.sdkAgentQuickstartP')}</Para>
          <CodeBlock title="Give an agent an identity, then report what it does">{`// Register the agent under an accountable operator
const agent = await client.registerAgent({
  operatorDid: 'did:adi:0xoperator...',
  modelInfo: { provider: 'Anthropic', name: 'claude-sonnet-5', version: '2026-02' },
  capabilities: [{ name: 'invoice.validate', description: 'Validates invoices' }],
  autonomyLevel: 0,                 // Intern — 0 to 3
});

// Report outcomes as the agent works. This is what moves its trust score.
await client.recordTrustSignal(agent.id, {
  type: 'task_completed',           // or task_failed, attestation, complaint, ...
  source: 'did:adi:0xyour-gateway',
  details: 'invoice #4471 validated',
});

// Read the current standing back
const { trustScore, autonomyLevel } = await client.getAgent(agent.id);`}</CodeBlock>
          <InfoBox>{t('sections.sdkErrorsInfo')}</InfoBox>

          <SubHeading id="sdk-errors">{t('sections.sdkErrors')}</SubHeading>
          <Para>{t('sections.sdkErrorsP')}</Para>
          <CodeBlock title="Handling failures">{`import { IDAHTTPError } from '@myida/sdk';

try {
  await client.issueCredential(params);
} catch (err) {
  if (err instanceof IDAHTTPError) {
    console.error(err.status, err.code, err.message);  // e.g. 400 INVALID_SCHEMA
  } else {
    throw err;
  }
}`}</CodeBlock>

          {/* === SMART CONTRACTS === */}
          <SectionHeading id="smart-contracts" icon="token">{t('sections.smartContractsHeading')}</SectionHeading>

          <SubHeading id="contracts-deployment-map">{t('sections.contractsDeploymentMap')}</SubHeading>
          <SimpleTable header={contractsDeploymentMapHeader} rows={contractsDeploymentMapRows} />

          <SubHeading id="did-registry">{t('sections.didRegistry')}</SubHeading>
          <Para>{t('sections.didRegistryP')}</Para>

          <SubHeading id="schema-registry">{t('sections.schemaRegistryDoc')}</SubHeading>
          <Para>{t('sections.schemaRegistryDocP')}</Para>

          <SubHeading id="revocation-registry">{t('sections.revocationRegistryDoc')}</SubHeading>
          <Para>{t('sections.revocationRegistryDocP')}</Para>

          <SubHeading id="agent-trust-registry">{t('sections.agentTrustRegistryDoc')}</SubHeading>
          <Para>{t('sections.agentTrustRegistryDocP')}</Para>

          <SubHeading id="zk-proof-verifier">{t('sections.zkProofVerifier')}</SubHeading>
          <Para>{t('sections.zkProofVerifierP')}</Para>

          <SubHeading id="contracts-interfaces">{t('sections.contractsInterfaces')}</SubHeading>
          <Para>{t('sections.contractsInterfacesP')}</Para>

          <SubHeading id="contracts-upgrade">{t('sections.contractsUpgrade')}</SubHeading>
          <Para>{t('sections.contractsUpgradeP')}</Para>

          {/* === RBAC === */}
          <SectionHeading id="rbac" icon="admin_panel_settings">{t('sections.rbacHeading')}</SectionHeading>

          <SubHeading id="roles-overview">{t('sections.rolesOverview')}</SubHeading>
          <Para>{t('sections.rolesOverviewP')}</Para>
          <div className="my-4 grid gap-4 sm:grid-cols-2">
            {roles.map((r) => {
              const cfg = roleColorMap[r.role] ?? roleColorMap.Holder;
              return (
                <div key={r.role} className={`rounded-lg border p-4 ${cfg.color}`}>
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined" style={{ fontSize: 22, fontVariationSettings: "'FILL' 1" }}>{cfg.icon}</span>
                    <span className="font-bold">{r.role}</span>
                  </div>
                  <p className="mt-2 text-sm opacity-80">{r.desc}</p>
                </div>
              );
            })}
          </div>

          <SubHeading id="permission-matrix">{t('sections.permissionMatrix')}</SubHeading>
          <div className="my-4 overflow-x-auto rounded-lg border border-border-subtle">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-subtle bg-slate-50">
                  {permissionHeader.map((h, i) => (
                    <th key={i} className={`px-4 py-3 ${i === 0 ? 'text-left' : 'text-center'} font-semibold text-text-main`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-text-muted">
                {permissionRows.map((row, ri) => (
                  <tr key={ri} className="border-b border-border-subtle last:border-0">
                    <td className="px-4 py-2.5 font-medium text-text-main">{row[0] as string}</td>
                    {(row.slice(1) as boolean[]).map((allowed, j) => (
                      <td key={j} className="px-4 py-2.5 text-center">
                        {allowed ? (
                          <span className="material-symbols-outlined text-emerald-500" style={{ fontSize: 18, fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                        ) : (
                          <span className="material-symbols-outlined text-slate-300" style={{ fontSize: 18 }}>remove</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <SubHeading id="role-navigation">{t('sections.roleNavigation')}</SubHeading>
          <Para>{t('sections.roleNavigationP1')}</Para>
          <Para>{t('sections.roleNavigationP2')}</Para>

          <SubHeading id="server-enforcement">{t('sections.serverEnforcement')}</SubHeading>
          <Para>{t('sections.serverEnforcementP')}</Para>

          <SubHeading id="jwt-lifecycle">{t('sections.jwtLifecycle')}</SubHeading>
          <Para>{t('sections.jwtLifecycleP')}</Para>

          <SubHeading id="api-keys">{t('sections.apiKeysSection')}</SubHeading>
          <Para>{t('sections.apiKeysSectionP')}</Para>

          <SubHeading id="security-controls">{t('sections.securityControls')}</SubHeading>
          <Para>{t('sections.securityControlsP')}</Para>

          {/* === DEPLOYMENT === */}
          <SectionHeading id="deployment" icon="cloud">{t('sections.deploymentHeading')}</SectionHeading>

          <SubHeading id="system-requirements">{t('sections.systemRequirements')}</SubHeading>
          <Para>{t('sections.systemRequirementsP')}</Para>

          <SubHeading id="docker-setup">{t('sections.dockerSetup')}</SubHeading>
          <Para>{t('sections.dockerSetupP')}</Para>
          <CodeBlock title="Docker (one-shot)">{`make dev          # dev overlay with Hardhat
make docker-up    # production
make docker-down  # tear down with volumes`}</CodeBlock>

          <SubHeading id="kubernetes-setup">{t('sections.kubernetesSetup')}</SubHeading>
          <Para>{t('sections.kubernetesSetupP')}</Para>

          <SubHeading id="configuration">{t('sections.configuration')}</SubHeading>
          <Para>{t('sections.configurationP')}</Para>

          <SubHeading id="env-vars">{t('toc.items.env-vars')}</SubHeading>
          <p className="mb-2 text-sm font-semibold text-text-main">Consumed by Go API server</p>
          <SimpleTable header={envConsumedHeader} rows={envConsumedRows} />
          <p className="mb-2 mt-6 text-sm font-semibold text-text-main">Informational / forward-compatibility</p>
          <SimpleTable header={envInformationalHeader} rows={envInformationalRows} />

          <SubHeading id="migrations-seed">{t('sections.migrationsSeed')}</SubHeading>
          <Para>{t('sections.migrationsSeedP')}</Para>
          <CodeBlock title="Migrations & seed">{`make db-migrate                     # run pending migrations
cd packages/api && go run ./cmd/seed`}</CodeBlock>

          <SubHeading id="contract-deployment">{t('sections.contractDeployment')}</SubHeading>
          <Para>{t('sections.contractDeploymentP')}</Para>
          <CodeBlock title="Hardhat deploy">{`cd packages/blockchain
npx hardhat run scripts/deploy.ts --network localhost
npx hardhat run scripts/deploy.ts --network adi_testnet
npx hardhat run scripts/deploy.ts --network adi_mainnet`}</CodeBlock>

          <SubHeading id="observability">{t('sections.observability')}</SubHeading>
          <Para>{t('sections.observabilityP')}</Para>

          <SubHeading id="backups-dr">{t('sections.backupsDr')}</SubHeading>
          <Para>{t('sections.backupsDrP')}</Para>

          <SubHeading id="scaling-notes">{t('sections.scalingNotes')}</SubHeading>
          <Para>{t('sections.scalingNotesP')}</Para>

          {/* === TROUBLESHOOTING === */}
          <SectionHeading id="troubleshooting" icon="troubleshoot">{t('sections.troubleshootingHeading')}</SectionHeading>

          <SubHeading id="common-errors">{t('sections.commonErrors')}</SubHeading>
          <Para>{t('sections.commonErrorsP')}</Para>
          <SimpleTable header={troubleshootingHeader} rows={troubleshootingRows} />

          <SubHeading id="did-resolution-issues">{t('sections.didResolutionIssues')}</SubHeading>
          <Para>{t('sections.didResolutionIssuesP')}</Para>

          <SubHeading id="credential-failures">{t('sections.credentialFailures')}</SubHeading>
          <Para>{t('sections.credentialFailuresP')}</Para>

          <SubHeading id="zkp-issues">{t('sections.zkpIssues')}</SubHeading>
          <Para>{t('sections.zkpIssuesP')}</Para>

          <SubHeading id="agent-issues">{t('sections.agentIssues')}</SubHeading>
          <Para>{t('sections.agentIssuesP')}</Para>

          <SubHeading id="didcomm-issues">{t('sections.didcommIssues')}</SubHeading>
          <Para>{t('sections.didcommIssuesP')}</Para>

          <SubHeading id="rpc-issues">{t('sections.rpcIssues')}</SubHeading>
          <Para>{t('sections.rpcIssuesP')}</Para>

          <SubHeading id="wallet-recovery-issues">{t('sections.walletRecoveryIssues')}</SubHeading>
          <Para>{t('sections.walletRecoveryIssuesP')}</Para>

          <SubHeading id="faq">{t('sections.faq')}</SubHeading>
          <SimpleTable header={faqHeader} rows={faqRows} />

          {/* === GLOSSARY === */}
          <SectionHeading id="glossary" icon="menu_book">{t('sections.glossaryHeading')}</SectionHeading>

          <SubHeading id="glossary-a-f">{t('sections.glossaryAfHeading')}</SubHeading>
          <SimpleTable header={['Term', 'Definition']} rows={glossaryAfRows} />

          <SubHeading id="glossary-g-m">{t('sections.glossaryGmHeading')}</SubHeading>
          <SimpleTable header={['Term', 'Definition']} rows={glossaryGmRows} />

          <SubHeading id="glossary-n-s">{t('sections.glossaryNsHeading')}</SubHeading>
          <SimpleTable header={['Term', 'Definition']} rows={glossaryNsRows} />

          <SubHeading id="glossary-t-z">{t('sections.glossaryTzHeading')}</SubHeading>
          <SimpleTable header={['Term', 'Definition']} rows={glossaryTzRows} />

          <div className="mt-20 border-t border-border-subtle pt-8 pb-12">
            <div className="flex items-center justify-between text-sm text-text-faint">
              <span>{t('footer.tagline')}</span>
              <Link to="/dashboard" className="flex items-center gap-1 font-semibold text-primary hover:underline">
                {t('footer.go')}
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>arrow_forward</span>
              </Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
